'use memo';

import { type OutboundCreatableDataType, OutboundCreatableDataTypeSchema } from '@standandforce/api-contract';
import { createFileRoute } from '@tanstack/react-router';
import { Suspense, useCallback, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/Card/card';
import Input from '@/components/Input/Input';
import { HStack, Spacer, VStack } from '@/components/Layout';
import { ViewportLoader } from '@/components/Loading';
import StepperNavigation from '@/components/Navigation/StepperNavigation';
import { ControlledSelect } from '@/components/Select/ControlledSelect';
import DockAddStepperAction from '@/components/Stepper/DockAddStepperAction';
import { Text } from '@/components/Typography/Text';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { FormField } from '@/routes/_staff/person/-components/form/FormField';
import { useInboundDataPipelineEnums } from '../../../-query';
import { useCreateOutboundDataPipeline } from './-query';
export const Route = createFileRoute('/_staff/settings/data-connector/outbound/data-pipeline/add/')({
  component: OutboundAddPipelinePage
});

/**
 * 作成可能な dataType は contract（`OutboundCreatableDataTypeSchema`）を SSoT とする。
 * 画面側で値を書き起こすと、BE が提供対象を増やしたときに選択肢へ出ないまま気づけない。
 */
const CREATABLE_DATA_TYPES: ReadonlySet<string> = new Set(OutboundCreatableDataTypeSchema.options);
const ADD_PIPELINE_LINK = '/settings/data-connector/outbound/data-pipeline/add';

/**
 * OUT パイプライン追加。IN add ページと同型のレイアウト：
 * 左に StepperNavigation（基本情報）＋右にフォームカード。文言は IN と共通の
 * i18n（inboundDataPipeline.add.*）を再利用する。OUT の入力は name / dataType のみ。
 */
function OutboundAddPipelinePage() {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.dataConnector.inboundDataPipeline.add'
  });
  useDocumentTitle(t('pageTitle'));
  return <HStack className="w-full p-6 h-[calc(100vh-64px)]">
      <InnerNavigation />
      <Spacer variant="m" />
      <Suspense fallback={<ViewportLoader />}>
        <OutboundAddPipelineForm />
      </Suspense>
    </HStack>;
}
function InnerNavigation() {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.dataConnector.inboundDataPipeline.add'
  });
  const sections = [{
    key: 'basicInfo',
    hash: 'basic-info',
    label: t('stepper.basicInfo'),
    completed: false,
    error: false
  }];
  return <StepperNavigation className="sticky h-full" link={{
    hash: 'basic-info',
    to: ADD_PIPELINE_LINK
  }} title={t('title')} sections={sections} />;
}
function OutboundAddPipelineForm() {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.dataConnector.inboundDataPipeline.add'
  });
  const {
    submit,
    isPending
  } = useCreateOutboundDataPipeline();
  const [name, setName] = useState('');
  // 初期選択は PEOPLE。contract から提供対象が外れたらここで型検査が落ちる。
  const [dataType, setDataType] = useState<OutboundCreatableDataType>('PEOPLE');

  // dataType の選択肢・ラベルは IN と同じ enums（サーバ多言語ラベル）を使い、
  // contract が作成可能とした種別だけに絞る（括弧付き enum 表記にしない）。
  const {
    data: enums
  } = useInboundDataPipelineEnums();
  const dataTypeOptions = useMemo(() => (enums?.dataTypes ?? []).filter(item => CREATABLE_DATA_TYPES.has(item.value)).map(item => ({
    value: item.value,
    label: item.label
  })), [enums]);
  const fetchDataTypeOptions = useCallback(() => Promise.resolve(dataTypeOptions), [dataTypeOptions]);
  const onSubmit = useCallback(() => {
    submit({
      name: name.trim(),
      dataType,
      operationMode: 'RUNNING',
      triggerMode: 'MANUAL',
      ingestionMode: 'FULL'
    });
  }, [name, dataType, submit]);
  return <VStack className="gap-6 w-full min-w-[340px]">
      <Card className="p-4 rounded-lg bg-neutral-fill outline-solid outline-1 -outline-offset-1 outline-neutral-muted-border shadow-md">
        <Text variant="h3">{`1. ${t('stepper.basicInfo')}`}</Text>
      </Card>

      <Card className="rounded-lg bg-neutral-fill outline-solid outline-1 -outline-offset-1 outline-neutral-muted-border shadow-md">
        <CardContent className="pt-6">
          <VStack className="gap-4">
            <FormField fieldTitle={t('form.name.label')} fieldInput={<Input id="name" value={name} width={320} placeholder={t('form.name.placeholder')} onChange={e => setName(e.target.value)} />} />
            <FormField fieldTitle={t('form.dataType.label')} fieldInput={<ControlledSelect id="dataType" value={dataType} onChange={option => {
            const selected = OutboundCreatableDataTypeSchema.safeParse(option?.value);
            if (selected.success) setDataType(selected.data);
          }} fetchOptions={fetchDataTypeOptions} hideSearch width={320} />} />
          </VStack>
        </CardContent>
      </Card>

      <DockAddStepperAction baseLink={ADD_PIPELINE_LINK} previousPage={null} nextPage={null} nextComplete disabled={isPending || name.trim() === ''} onNextClick={onSubmit} onCompleteClick={onSubmit} />
    </VStack>;
}